import { DOM_IDS } from "@circle-react/components/shared/uikit/CircleAIAppShell/Layout/defaults";
import { SideDrawer } from "@circleco/compass/components/SideDrawer";
import { useLayoutEffect, useRef } from "react";

import { t } from "@/i18n-js/instance";
import { LazyMemberChecklistContent } from "@/react/components/MemberChecklist/LazyMemberChecklistContent";
import type { MemberChecklistItemWithCompletion } from "@/react/types/MemberChecklistItem";

interface MemberChecklistSideDrawerProps {
  container?: HTMLElement | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  previewMemberChecklistItems?: MemberChecklistItemWithCompletion[];
}

const rootLocale = "header.member_checklist";

export const MemberChecklistSideDrawer = ({
  open,
  onOpenChange,
  container,
  previewMemberChecklistItems,
}: MemberChecklistSideDrawerProps) => {
  const drawerContentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || container) return;

    const workspace = document.getElementById(DOM_IDS.workspaceBody);
    const drawerContent = drawerContentRef.current;

    if (!workspace || !drawerContent) return;

    const updateDrawerContentPosition = () => {
      const { top, right } = workspace.getBoundingClientRect();
      drawerContent.style.top = `${top}px`;
      drawerContent.style.right = `${window.innerWidth - right}px`;
    };

    updateDrawerContentPosition();

    const resizeObserver = new ResizeObserver(updateDrawerContentPosition);
    resizeObserver.observe(workspace);
    window.addEventListener("resize", updateDrawerContentPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDrawerContentPosition);
    };
  }, [open, container, drawerContentRef.current]);

  return (
    <SideDrawer.Root
      open={open}
      onOpenChange={onOpenChange}
      container={container}
      noBackdrop
      size="md"
    >
      <SideDrawer.Content ref={drawerContentRef}>
        <SideDrawer.Header title={t([rootLocale, "title"])} />
        <SideDrawer.Body>
          <LazyMemberChecklistContent
            enabled={open}
            closeChecklist={() => onOpenChange(false)}
            previewMemberChecklistItems={previewMemberChecklistItems}
          />
        </SideDrawer.Body>
      </SideDrawer.Content>
    </SideDrawer.Root>
  );
};
